import { supabase } from "./core.js";
import type { PatrimonioExtraItem, NetWorthData } from "../types/index.js";

interface AssetTypeConfig {
  label: string;
  icon: string;
  isLiability: boolean;
  color: string;
  grupo: string;
}

export const ASSET_TYPES: Record<string, AssetTypeConfig> = {
  imovel: {
    label: "Imóvel",
    icon: "home",
    isLiability: false,
    color: "#4CAF50",
    grupo: "patrimonio",
  },
  veiculo: {
    label: "Veículo",
    icon: "car",
    isLiability: false,
    color: "#2196F3",
    grupo: "patrimonio",
  },
  previdencia: {
    label: "Previdência",
    icon: "umbrella",
    isLiability: false,
    color: "#9C27B0",
    grupo: "previdencia",
  },
  seguro: {
    label: "Seguro de Vida",
    icon: "shield",
    isLiability: false,
    color: "#FF9800",
    grupo: "protecao",
  },
  participacao: {
    label: "Participação Societária",
    icon: "briefcase",
    isLiability: false,
    color: "#607D8B",
    grupo: "patrimonio",
  },
  outros_ativos: {
    label: "Outros Ativos",
    icon: "plus-circle",
    isLiability: false,
    color: "#795548",
    grupo: "patrimonio",
  },
  financiamento: {
    label: "Financiamento",
    icon: "credit-card",
    isLiability: true,
    color: "#F44336",
    grupo: "passivo",
  },
  emprestimo: {
    label: "Empréstimo",
    icon: "dollar-sign",
    isLiability: true,
    color: "#E91E63",
    grupo: "passivo",
  },
  divida: {
    label: "Dívida",
    icon: "alert-triangle",
    isLiability: true,
    color: "#FF5722",
    grupo: "passivo",
  },
  outros_passivos: {
    label: "Outros Passivos",
    icon: "minus-circle",
    isLiability: true,
    color: "#9E9E9E",
    grupo: "passivo",
  },
};

export const ASSET_ONLY = Object.fromEntries(
  Object.entries(ASSET_TYPES).filter(([, v]) => !v.isLiability)
);

export const LIABILITY_ONLY = Object.fromEntries(
  Object.entries(ASSET_TYPES).filter(([, v]) => v.isLiability)
);

export async function fetchPatrimonioExtra(clientId: string): Promise<PatrimonioExtraItem[]> {
  const { data, error } = await supabase
    .from("patrimonio_extra")
    .select("*")
    .eq("client_id", clientId)
    .order("valor", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function upsertPatrimonioExtra(
  clientId: string,
  item: Partial<PatrimonioExtraItem>
): Promise<PatrimonioExtraItem> {
  const { data, error } = await supabase
    .from("patrimonio_extra")
    .upsert(
      { ...item, client_id: clientId, updated_at: new Date().toISOString() },
      { onConflict: "id" }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePatrimonioExtra(id: string): Promise<void> {
  const { error } = await supabase.from("patrimonio_extra").delete().eq("id", id);
  if (error) throw error;
}

export function calculateNetWorth(
  investimentos: number,
  extras: PatrimonioExtraItem[]
): NetWorthData {
  let totalAssets = investimentos;
  let totalLiabilities = 0;

  for (const item of extras) {
    const val = item.valor || 0;
    const config = ASSET_TYPES[item.tipo || ""];
    if (config?.isLiability) {
      totalLiabilities += val;
    } else {
      totalAssets += val;
    }
  }

  return {
    investimentos,
    outrosAtivos: totalAssets - investimentos,
    totalAssets: totalAssets,
    totalLiabilities: totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
  };
}

interface AssetBreakdownItem {
  tipo: string;
  label: string;
  total: number;
  count: number;
  isLiability: boolean;
}

export function getAssetBreakdown(extras: PatrimonioExtraItem[]): AssetBreakdownItem[] {
  const grouped: Record<string, { total: number; count: number }> = {};

  for (const item of extras) {
    const tipo = item.tipo || "outros_ativos";
    if (!grouped[tipo]) grouped[tipo] = { total: 0, count: 0 };
    grouped[tipo].total += item.valor || 0;
    grouped[tipo].count++;
  }

  return Object.entries(grouped)
    .map(([tipo, v]) => ({
      tipo,
      label: ASSET_TYPES[tipo]?.label || tipo,
      total: v.total,
      count: v.count,
      isLiability: ASSET_TYPES[tipo]?.isLiability || false,
    }))
    .sort((a, b) => b.total - a.total);
}
